import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { bandColor } from "./seoUtils";

export default function SEOPageList({
  items, total, loading, query, onQuery, filters, onFilter, onOpen, onAdd, onDiscover,
  selected, onToggle, onBulkAnalyze, onBulkPublish, onPublishCore, onPublishAllDrafts, onPublishBlog, onSelectAllDrafts, draftCount, onAnalyzeOne, onExport, canPublish,
}) {
  const allIds = useMemo(() => (items || []).map((p) => p.id), [items]);
  const [showBulk, setShowBulk] = useState(false);
  const empty = !loading && (!items || items.length === 0);

  return (
    <GlassCard className="p-0 overflow-hidden w-full max-w-[1600px]">
      <div className="px-4 py-3 border-b border-white/5 text-xs text-white/50">
        Click a page → edit title &amp; description → Save → Publish.
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 p-4 border-b border-white/5 items-stretch sm:items-center">
        <Input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search pages…" className="bg-black/20 border-white/10 text-white w-full sm:max-w-xs" />
        <select value={filters.status || ""} onChange={(e) => onFilter("status", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
          <option value="">All</option>
          <option value="draft">Drafts only</option>
          <option value="published">Published only</option>
        </select>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
        <Button onClick={onAdd} className="btn-gradient text-white border-0"><Icons.Plus className="w-4 h-4 mr-1" /> Add URL</Button>
        <Button variant="ghost" onClick={onDiscover} className="text-white/70 hover:text-white">Find pages</Button>
        <button type="button" className="text-xs text-white/40 hover:text-white/70" onClick={() => setShowBulk((v) => !v)}>
          {showBulk ? "Hide bulk actions" : "Bulk actions…"}
        </button>
        </div>
      </div>
      {showBulk && (
        <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
          <Button variant="ghost" onClick={onBulkAnalyze} disabled={!selected.size} className="text-white/70 hover:text-white disabled:opacity-40">Analyze selected</Button>
          <Button variant="ghost" onClick={onBulkPublish} disabled={!selected.size || !canPublish} className="text-white/70 hover:text-white disabled:opacity-40">Publish selected</Button>
          {canPublish && (
            <>
              <Button variant="ghost" onClick={onSelectAllDrafts} disabled={!draftCount} className="text-white/70 hover:text-white disabled:opacity-40">Select drafts ({draftCount || 0})</Button>
              <Button variant="ghost" onClick={onPublishAllDrafts} disabled={!draftCount} className="text-white/70 hover:text-white disabled:opacity-40">Publish all drafts</Button>
              <Button variant="ghost" onClick={onPublishCore} className="text-white/70 hover:text-white">Publish core</Button>
              <Button variant="ghost" onClick={onPublishBlog} className="text-white/70 hover:text-white">Publish blog</Button>
            </>
          )}
          <Button variant="ghost" onClick={onExport} className="text-white/70 hover:text-white">Export CSV</Button>
          <select value={filters.page_type} onChange={(e) => onFilter("page_type", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
            <option value="">All types</option>
            {["landing", "service", "portfolio", "blog", "about", "contact", "custom"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.source} onChange={(e) => onFilter("source", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
            <option value="">All sources</option>
            {["cms", "manual", "crawler", "sitemap"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-white/40 bg-white/[0.02]">
            <tr>
              {showBulk && <th className="px-4 py-2"><input type="checkbox" aria-label="Select all" onChange={(e) => allIds.forEach((id) => onToggle(id, e.target.checked))} /></th>}
              <th className="px-4 py-2 text-left">Page</th>
              <th className="px-4 py-2 text-left">Score</th>
              <th className="px-4 py-2 text-left">Live?</th>
              <th className="px-4 py-2 text-left" />
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40">Loading pages…</td></tr> : null}
            {!loading && (items || []).map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => onOpen(p)}>
                {showBulk && (
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={(e) => onToggle(p.id, e.target.checked)} aria-label={`Select ${p.name}`} />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="text-white">{p.name}</div>
                  <div className="font-mono text-[11px] text-white/40">{p.path}</div>
                </td>
                <td className="px-4 py-3 font-medium" style={{ color: bandColor(p.seo_score) }}>{p.seo_score ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${(p.publish_state || p.status) === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/50"}`}>
                    {(p.publish_state || p.status) === "published" ? "Yes" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="text-xs text-cyan-300/80 hover:text-cyan-200" onClick={() => onOpen(p)}>Edit</button>
                </td>
              </tr>
            ))}
            {empty && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <p className="text-white/50 mb-4">No pages yet. Find them on the live site, or paste one URL.</p>
                  <div className="flex justify-center gap-2">
                    <Button onClick={onDiscover} className="btn-gradient text-white border-0">Find pages</Button>
                    <Button variant="ghost" onClick={onAdd} className="text-white/70">Add URL</Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 text-xs text-white/40">{total || 0} pages{showBulk ? ` · ${selected.size} selected` : ""}</div>
    </GlassCard>
  );
}
